const API_BASE = "/api/v1"

interface ApiError {
  status: number
  message: string
  errors?: Record<string, string[]>
  [key: string]: unknown
}

class ApiClientError extends Error {
  status: number
  errors?: Record<string, string[]>
  missing_fields?: string[]
  [key: string]: unknown

  constructor({ status, message, errors, ...rest }: ApiError) {
    super(message)
    this.name = "ApiClientError"
    this.status = status
    this.errors = errors
    Object.assign(this, rest)
  }
}

function getCsrfToken(): string | null {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") ?? null
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const url = path.startsWith("/") ? path : `${API_BASE}/${path}`

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(options.headers as Record<string, string>),
  }

  const csrfToken = getCsrfToken()
  if (csrfToken) {
    headers["X-CSRF-Token"] = csrfToken
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: "same-origin",
  })

  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new ApiClientError({
      status: response.status,
      message: body.error || body.message || `Request failed: ${response.status}`,
      errors: body.errors,
      ...body,
    })
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

async function uploadRequest<T>(
  path: string,
  formData: FormData
): Promise<T> {
  const url = path.startsWith("/") ? path : `${API_BASE}/${path}`

  const headers: Record<string, string> = {
    Accept: "application/json",
  }

  const csrfToken = getCsrfToken()
  if (csrfToken) {
    headers["X-CSRF-Token"] = csrfToken
  }

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: formData,
    credentials: "same-origin",
  })

  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new ApiClientError({
      status: response.status,
      message: body.error || body.message || `Upload failed: ${response.status}`,
      errors: body.errors,
      ...body,
    })
  }

  return response.json()
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: "GET" }),

  post: <T>(path: string, data?: unknown) =>
    request<T>(path, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: <T>(path: string, data?: unknown) =>
    request<T>(path, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T>(path: string, data?: unknown) =>
    request<T>(path, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),

  upload: <T>(path: string, formData: FormData) => uploadRequest<T>(path, formData),
}

export { ApiClientError }
export type { ApiError }
